import React from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon, 
  ChartBarIcon, 
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const TokenizedInvestments = () => {
  const features = [
    {
      icon: CurrencyDollarIcon,
      title: "€4,000 Investment Power",
      description: "Each NFT represents €4,000 in investment capacity for project participation",
      color: "from-green-400 to-emerald-600"
    },
    {
      icon: ChartBarIcon,
      title: "50% Profit Share",
      description: "NFT holders receive 50% of all project profits distributed proportionally",
      color: "from-purple-400 to-violet-600"
    },
    {
      icon: ShieldCheckIcon,
      title: "Blockchain Security",
      description: "Transparent, immutable ownership records and profit distribution",
      color: "from-cyan-400 to-blue-600"
    },
    {
      icon: UserGroupIcon,
      title: "Exclusive Community",
      description: "VIP access to investment opportunities not available to the public",
      color: "from-pink-400 to-rose-600"
    }
  ];

  const investmentProcess = [
    {
      step: "1",
      title: "Mint Your NFT",
      description: "Purchase Octagon Infinity NFT for 0.24 ETH",
      color: "purple"
    },
    {
      step: "2",
      title: "Gain VIP Access",
      description: "Unlock exclusive investment opportunities",
      color: "cyan"
    },
    {
      step: "3",
      title: "Choose Projects",
      description: "Select from vetted European real estate projects",
      color: "green"
    },
    {
      step: "4",
      title: "Earn Returns",
      description: "Receive 50% of project profits automatically",
      color: "yellow"
    }
  ];

  return (
    <section id="investments" className="py-20 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1630463853299-75a5f73da1ca"
          alt="Tokenized Investment"
          className="w-full h-full object-cover opacity-5"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Tokenized Investments
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Revolutionary approach to real estate investment through NFT ownership. 
            Your NFT is more than digital art—it's your key to premium investment opportunities.
          </p>
        </motion.div>

        {/* Investment Features */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700 hover:border-purple-500 transition-all duration-300 text-center"
              whileHover={{ scale: 1.05, y: -10 }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${feature.color} flex items-center justify-center`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Investment Process */}
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3 className="text-4xl font-bold text-center mb-12 text-white">
            How It Works
          </h3>

          <div className="grid md:grid-cols-4 gap-8">
            {investmentProcess.map((process, index) => (
              <motion.div
                key={index}
                className="relative text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Connection Line */}
                {index < investmentProcess.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-1 bg-gradient-to-r from-purple-500 to-cyan-500 transform -translate-y-1/2 z-0"></div>
                )}

                <motion.div
                  className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${
                    process.color === 'purple' ? 'from-purple-500 to-purple-600' :
                    process.color === 'cyan' ? 'from-cyan-500 to-cyan-600' :
                    process.color === 'green' ? 'from-green-500 to-green-600' :
                    'from-yellow-500 to-yellow-600'
                  } flex items-center justify-center text-2xl font-bold text-white relative z-10`}
                  whileHover={{ scale: 1.1 }}
                >
                  {process.step}
                </motion.div>

                <h4 className="text-xl font-semibold mb-3 text-white">{process.title}</h4>
                <p className="text-gray-400 leading-relaxed">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Profit Distribution Breakdown */}
        <motion.div
          className="mt-20 bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-lg rounded-3xl p-8 border border-gray-600"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-bold text-center mb-8 text-white">
            Profit Distribution Model
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="text-center p-6 bg-gradient-to-r from-purple-600/20 to-purple-700/20 rounded-2xl border border-purple-500/30"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-purple-400 mb-2">50%</div>
              <div className="text-xl font-semibold text-white mb-2">NFT Holders</div>
              <div className="text-gray-400">Distributed to Cards owners based on participation</div>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-gradient-to-r from-cyan-600/20 to-cyan-700/20 rounded-2xl border border-cyan-500/30"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-cyan-400 mb-2">20%</div>
              <div className="text-xl font-semibold text-white mb-2">Development</div>
              <div className="text-gray-400">R&D, workforce, and marketing investments</div>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-gradient-to-r from-green-600/20 to-green-700/20 rounded-2xl border border-green-500/30"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-4xl font-bold text-green-400 mb-2">30%</div>
              <div className="text-xl font-semibold text-white mb-2">Reinvestment</div>
              <div className="text-gray-400">Expanding portfolio and future opportunities</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TokenizedInvestments;